import { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, m } from 'motion/react';
import { Blanket, Box } from '@customerio/pluma-components/react';

interface LightboxProps {
  /** The image source URL. When non-null the lightbox is visible. */
  src: string | null;
  /** Called when the lightbox should close. */
  onClose: () => void;
}

/**
 * Full-screen image lightbox with a dark backdrop.
 * Fades in/out with a subtle scale animation. Closes on click or Escape.
 */
export function Lightbox({ src, onClose }: LightboxProps) {
  useEffect(() => {
    if (src == null) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [src, onClose]);

  return (
    <AnimatePresence>
      {src != null ? (
        <m.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          style={{ position: 'fixed', inset: 0, zIndex: 9999 }}
        >
          <Blanket withBackground={true} onClick={onClose}>
            <Box display="flex" alignItems="center" justifyContent="center" h="full" w="full">
              <m.img
                src={src}
                alt=""
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                transition={{ duration: 0.15 }}
                style={{
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  cursor: 'zoom-out',
                }}
                onClick={onClose}
              />
            </Box>
          </Blanket>
        </m.div>
      ) : null}
    </AnimatePresence>
  );
}

/**
 * Hook to manage lightbox state. Returns the current src, an open handler,
 * and a close handler.
 */
export function useLightbox() {
  const [source, setSource] = useState<string | null>(null);
  const open = useCallback((source: string) => setSource(source), []);
  const close = useCallback(() => setSource(null), []);
  return { src: source, open, close };
}
